import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaVideo, FaClock, FaCheck, FaArrowRight, FaBook, FaUserTie, FaLightbulb, FaRegCalendarAlt } from 'react-icons/fa';

const Introducing = () => {
  return (
    <IntroducingSection>
      <div className="container">
        <motion.h2 
          className="section-title"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          style={{ color: 'var(--primary-color)' }}
        >
          Introducing Dreams AI Can Buy
        </motion.h2>
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <SectionDescription>
            AI is transforming the way we work, create, and automate—but where do you start?
          </SectionDescription>
          
          <FeatureCard>
            <DescriptionText>
              Dreams AI Can Buy is a 90-minute, one-on-one video session designed to give you hands-on experience with AI tools that can supercharge your productivity and creativity.
            </DescriptionText>
            
            <FeatureBoxes>
              <FeatureBox>
                <FeatureIcon>
                  <FaVideo color="var(--primary-color)" size={32} />
                </FeatureIcon>
                <FeatureTitle>Personalized Sessions</FeatureTitle>
                <FeatureDescription>
                  Tailored learning experience based on your specific needs and current AI knowledge level.
                </FeatureDescription>
              </FeatureBox>
              
              <FeatureBox>
                <FeatureIcon>
                  <FaClock color="var(--primary-color)" size={32} />
                </FeatureIcon>
                <FeatureTitle>90-Minute Deep Dive</FeatureTitle>
                <FeatureDescription>
                  Comprehensive yet focused session that respects your time while delivering maximum value.
                </FeatureDescription>
              </FeatureBox>
            </FeatureBoxes>
          </FeatureCard>
          
          <SectionSubtitle>What to Expect from Your Session</SectionSubtitle>
          
          <ExpectationsList>
            <ExpectationItem>
              <CheckIcon><FaCheck /></CheckIcon>
              <ExpectationContent>
                <ExpectationTitle>Personalized Learning:</ExpectationTitle> Tailored to your needs and AI experience.
              </ExpectationContent>
            </ExpectationItem>
            
            <ExpectationItem>
              <CheckIcon><FaCheck /></CheckIcon>
              <ExpectationContent>
                <ExpectationTitle>Clear, Non-Technical Explanations:</ExpectationTitle> Understand AI without jargon.
              </ExpectationContent>
            </ExpectationItem>
            
            <ExpectationItem>
              <CheckIcon><FaCheck /></CheckIcon>
              <ExpectationContent>
                <ExpectationTitle>Exploring AI Models:</ExpectationTitle> Learn about GPT-4o, Claude 3.7, and Gemini.
              </ExpectationContent>
            </ExpectationItem>
            
            <ExpectationItem>
              <CheckIcon><FaCheck /></CheckIcon>
              <ExpectationContent>
                <ExpectationTitle>Best Practices & Pitfalls:</ExpectationTitle> Improve AI interactions and avoid common mistakes.
              </ExpectationContent>
            </ExpectationItem>
            
            <ExpectationItem>
              <CheckIcon><FaCheck /></CheckIcon>
              <ExpectationContent>
                <ExpectationTitle>Live Tool Demonstrations:</ExpectationTitle> Hands-on experience with premium AI models.
              </ExpectationContent>
            </ExpectationItem>
            
            <ExpectationItem>
              <CheckIcon><FaCheck /></CheckIcon>
              <ExpectationContent>
                <ExpectationTitle>Session Recording & AI Summary:</ExpectationTitle> Get a full recording and summary for reference.
              </ExpectationContent>
            </ExpectationItem>
            
            <ExpectationItem>
              <CheckIcon><FaCheck /></CheckIcon>
              <ExpectationContent>
                <ExpectationTitle>Post-Session Guide:</ExpectationTitle> A handy guide covering key AI concepts.
              </ExpectationContent>
            </ExpectationItem>
          </ExpectationsList>
          
          <SectionSubtitle>Who Is This For?</SectionSubtitle>
          
          <AudienceGrid>
            <AudienceCard>
              <AudienceContent>
                <AudienceIcon>
                  <FaBook size={28} />
                </AudienceIcon>
                <AudienceTitle>Professionals</AudienceTitle>
              </AudienceContent>
            </AudienceCard>
            
            <AudienceCard>
              <AudienceContent>
                <AudienceIcon>
                  <FaUserTie size={28} />
                </AudienceIcon>
                <AudienceTitle>Entrepreneurs</AudienceTitle>
              </AudienceContent>
            </AudienceCard>
            
            <AudienceCard>
              <AudienceContent>
                <AudienceIcon>
                  <FaVideo size={28} />
                </AudienceIcon>
                <AudienceTitle>Creatives</AudienceTitle>
              </AudienceContent>
            </AudienceCard>
            
            <AudienceCard>
              <AudienceContent>
                <AudienceIcon>
                  <FaLightbulb size={28} />
                </AudienceIcon>
                <AudienceTitle>Curious Minds</AudienceTitle>
              </AudienceContent>
            </AudienceCard>
          </AudienceGrid>
          
          <CTABG>
            <CTAContainer>
              <CTATitle>Ready to Transform Your AI Journey?</CTATitle>
              <CTAText>
                By the end of your session, you'll have a clear roadmap for using AI in your personal and professional life—whether it's streamlining work, automating tasks, or even planning your next holiday!
              </CTAText>
              <CTAButton onClick={() => document.getElementById('booking').scrollIntoView({ behavior: 'smooth' })}>
                Book Your Session <FaArrowRight style={{ marginLeft: '10px' }} />
              </CTAButton>
            </CTAContainer>
          </CTABG>
        </motion.div>
      </div>
    </IntroducingSection>
  );
};

const IntroducingSection = styled.section`
  background: linear-gradient(90deg, #f8f9fa 0%, #f6f7f8 100%);
  text-align: center;
`;

const SectionDescription = styled.p`
  font-size: 1.2rem;
  max-width: 1035px;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const FeatureCard = styled.div`
  background-color: var(--white);
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  padding: 2rem;
  margin: 0 auto 3rem;
  max-width: 960px;
`;

const DescriptionText = styled.p`
  font-size: 1.2rem;
  max-width: 850px;
  margin: 0 auto 2rem;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const FeatureBoxes = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureBox = styled.div`
  background-color: #f8f9ff;
  border-radius: 8px;
  padding: 1.5rem;
  text-align: left;
`;

const FeatureIcon = styled.div`
  margin-bottom: 1rem;
`;

const FeatureTitle = styled.h4`
  font-size: 1.3rem;
  color: var(--dark-blue);
  margin-bottom: 0.75rem;
`;

const FeatureDescription = styled.p`
  font-size: 1rem;
  color: var(--dark-blue);
`;

const SectionSubtitle = styled.h3`
  font-size: 1.8rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const ExpectationsList = styled.div`
  max-width: 920px;
  margin: 0 auto 2rem;
  background-color: var(--white);
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  padding: 2rem;
`;

const ExpectationItem = styled.div`
  display: flex;
  align-items: baseline;
  margin-bottom: 1.25rem;
  text-align: left;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const CheckIcon = styled.div`
  color: var(--green);
  font-size: 1.1rem;
  margin-right: 0.8rem;
  flex-shrink: 0;
`;

const ExpectationContent = styled.div`
  flex: 1;
`;

const ExpectationTitle = styled.span`
  font-weight: 600;
`;

const AudienceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  max-width: 920px;
  margin: 0 auto 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const AudienceCard = styled.div`
  background-color: var(--white);
  border-radius: 12px;
  padding: 1.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
`;

const AudienceContent = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const AudienceIcon = styled.div`
  font-size: 1.8rem;
  color: var(--primary-color);
  display: flex;
  align-items: center;
`;

const AudienceTitle = styled.h4`
  font-size: 1.3rem;
  margin: 0;
  color: var(--dark-blue);
`;

const CTABG = styled.div`
  background-color: var(--primary-color);
  border-radius: 12px;
  padding: 3rem 2rem;
  margin: 0 auto;
  max-width: 920px;
  color: white;
`;

const CTAContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
`;

const CTATitle = styled.h3`
  font-size: 2rem;
  margin-bottom: 1.5rem;
  color: white;
  
  @media (max-width: 768px) {
    font-size: 1.7rem;
  }
`;

const CTAText = styled.p`
  font-size: 1.2rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const CTAButton = styled.button`
  background-color: white;
  color: var(--primary-color);
  font-weight: bold;
  padding: 14px 28px;
  border: none;
  border-radius: 4px;
  font-size: 1.1rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  transition: var(--transition);
  
  &:hover {
    background-color: #f8f9fa;
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
  }
`;

export default Introducing;
